import { useContext, useMemo } from 'react';
import { kebabCase } from 'lodash-es';
import { trackError } from '../components/common/error.utils';
import { TestIdBoundaryContext } from '../components/common/providers/test-id-boundary.provider';

type DataTestIdPartName = 'prefix' | 'identifier' | 'suffix';

interface IGetTestIdErrorLogProps {
    value: string;
    valueName: DataTestIdPartName;
    enforce: boolean;
    testId: string;
    prefix: string;
    identifier: string;
    suffix: string;
    componentName: string;
    testIdType: string;
}

export const SILENT_MODE = false;

function getMiddleMessageOfDataTestIdError(valueName: DataTestIdPartName) {
    if (valueName === 'prefix') {
        return `you must wrap your component with 'TestIdBoundaryProvider'`;
    }

    if (valueName === 'identifier') {
        return 'you must provide an identifier like: testIdName or id or name to your component';
    }

    return 'you need to supply in the ui component the suffix type';
}

const SAMPLE_RATE = 100;

function validateDataTestIdPartValue({
    value,
    valueName,
    enforce,
    testId,
    prefix,
    identifier,
    suffix,
    componentName,
    testIdType
}: IGetTestIdErrorLogProps) {
    if (value || testIdType) {
        return true;
    }

    if (SILENT_MODE) {
        return false;
    }

    const sampleNumber = Math.floor(Math.random() * SAMPLE_RATE);
    if (sampleNumber !== SAMPLE_RATE - 1) {
        return false;
    }

    const startOfMessage = `data-testid error: ${componentName} is missing the ${valueName} for the 'data-testid' prop`;
    const middleOfMessage = getMiddleMessageOfDataTestIdError(valueName);
    const endOfMessage = `prefix: ${prefix}; identifier: ${identifier}; suffix: ${suffix};`;
    const log = `${startOfMessage}, ${middleOfMessage}. ${endOfMessage}`;
    // Use an Error object so DataDog captures the JS stack trace (handling_stack).
    // On React 17 production the stack terminates at the reconciler — the originating
    // consumer is not visible. Identify offenders by componentName + prefix + suffix.
    const error = new Error(log);
    try {
        trackError(error, {
            componentName,
            testId,
            identifier,
            suffix
        });
    } catch {
        // eslint-disable-next-line no-console
        console.error(error);
    }

    if (enforce) {
        throw error;
    }

    return false;
}

export interface IProps {
    identifier: string;
    suffix: string;
    componentName: string;
    testId?: string;
    enforce?: boolean;
    testIdType?: string;
}

export function useBuildTestIdInternal({
    identifier,
    suffix,
    componentName,
    testId,
    enforce,
    testIdType
}: IProps): string {
    const { prefix } = useContext(TestIdBoundaryContext);

    return useMemo(() => {
        if (testId) {
            return testId;
        }

        const commonValidateProps = {
            testId,
            prefix,
            identifier,
            suffix,
            enforce,
            componentName,
            testIdType
        };

        let isValid = validateDataTestIdPartValue({
            value: prefix,
            valueName: 'prefix',
            ...commonValidateProps
        });

        isValid =
            isValid &&
            validateDataTestIdPartValue({
                value: identifier,
                valueName: 'identifier',
                ...commonValidateProps
            });

        isValid =
            isValid &&
            validateDataTestIdPartValue({
                value: suffix,
                valueName: 'suffix',
                ...commonValidateProps
            });

        if (!isValid) {
            return;
        }

        // We allow interactable to have test id type without test id for action buttons that exists in HBToast, HBDialog etc.
        // You can use TestIdType just inside this package.
        const isInteractableActionWithJustTestIdType =
            testIdType && (!prefix || !identifier || !suffix);
        if (isInteractableActionWithJustTestIdType) {
            return;
        }

        const formattedIdentifier = kebabCase(identifier);

        return `${prefix}-${formattedIdentifier}--${suffix}`;
    }, [
        testId,
        prefix,
        identifier,
        suffix,
        enforce,
        componentName,
        testIdType
    ]);
}
